// frontend/src/pages/Auth.tsx

import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabaseClient';
import {
  Mail,
  Lock,
  User,
  MapPin,
  Building2,
  Sparkles,
  ShieldAlert,
  ArrowLeft,
  GraduationCap,
  Smile,
  Compass,
  Phone,
  Check,
  X,
  KeyRound,
  Eye,
  EyeOff
} from 'lucide-react';
import '../styles/auth.css';
import { ShaderBackground } from '../components/ui/manu';

type Mode = 'login' | 'register-student' | 'register-parent' | 'school' | 'admin' | 'forgot-password' | 'reset-password';

const ADMIN_EMAIL = 'minikcoderakademi@gmail.com';
const ADMIN_PASSWORD = 'Minik160erem?*Ace.';

// Official MinikCoder Akademi Logo SVG Component
const LogoSVG = () => (
  <svg width="52" height="52" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="auth-logo-svg">
    <defs>
      <linearGradient id="authBlue" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="50%" stopColor="#0284C7" />
        <stop offset="100%" stopColor="#0369A1" />
      </linearGradient>
      <linearGradient id="authTeal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#17C79A" />
        <stop offset="100%" stopColor="#0B8F70" />
      </linearGradient>
    </defs>
    <path d="M 35 150 L 35 60 L 80 110 L 110 80 L 145 120 L 145 150" stroke="url(#authBlue)" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M 110 60 L 155 60 C 170 60 170 85 150 100 L 115 125 L 155 170" stroke="url(#authTeal)" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="95" cy="95" r="14" fill="#17C79A" />
    <path d="M 68 128 Q 95 102 122 128" stroke="#38BDF8" strokeWidth="12" strokeLinecap="round" />
    <rect x="150" y="25" width="12" height="12" rx="2" fill="#17C79A" />
    <rect x="168" y="10" width="8" height="8" rx="1.5" fill="#38BDF8" />
    <rect x="170" y="32" width="6" height="6" rx="1" fill="#0B8F70" />
  </svg>
);

export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { session, profile } = useAuthStore();

  // Ban ile yönlendirildiyse otomatik olarak hata göster
  useEffect(() => {
    if ((location.state as any)?.bannedError) {
      setError('Hesabınız askıya alınmıştır. Daha fazla bilgi için lütfen destek ekibimizle iletişime geçin.');
      // State'i temizle (sayfayı yenilerse tekrar göstermemek için)
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  const [mode, setMode] = useState<Mode>(() => {
    const m = searchParams.get('mode');
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    if (
      m === 'reset-password' ||
      hash.includes('type=recovery') ||
      hash.includes('access_token') ||
      search.includes('type=recovery') ||
      search.includes('code=')
    ) {
      return 'reset-password';
    }
    if (m === 'login' || m === 'register-student' || m === 'register-parent' || m === 'admin' || m === 'school' || m === 'forgot-password') {
      return m as Mode;
    }
    return 'login';
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [age, setAge] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [city, setCity] = useState('');
  const [interests, setInterests] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Şifre Güvenlik Politikası Denetimi (Step 5)
  const isMinLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const isPasswordStrong = isMinLength && hasUpper && hasNumber && hasSpecial;

  const isRecoveryStore = useAuthStore((s) => s.isRecovery);

  useEffect(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const isRecovery =
      isRecoveryStore ||
      mode === 'reset-password' ||
      hash.includes('type=recovery') ||
      hash.includes('access_token') ||
      search.includes('type=recovery') ||
      search.includes('code=');

    if (isRecovery) {
      navigate('/reset-password', { replace: true });
    } else if (session && profile) {
      navigate('/');
    }
  }, [session, profile, navigate, mode, searchParams, isRecoveryStore]);

  useEffect(() => {
    // Supabase Auth PASSWORD_RECOVERY listener & URL recovery check
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        useAuthStore.getState().setRecovery(true);
        navigate('/reset-password', { replace: true });
      }
    });

    const hash = window.location.hash || '';
    const search = window.location.search || '';
    if (
      isRecoveryStore ||
      hash.includes('type=recovery') ||
      hash.includes('access_token') ||
      search.includes('type=recovery') ||
      search.includes('code=')
    ) {
      navigate('/reset-password', { replace: true });
    }

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [searchParams, isRecoveryStore, navigate]);

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);

    if (!email.trim()) {
      setError('Lütfen e-posta adresinizi girin.');
      return;
    }

    setIsLoading(true);
    try {
      await authService.requestPasswordReset(email.trim());
      setInfoMessage(
        'Eğer bilgileriniz sistemimizde kayıtlıysa, size özel bir şifre sıfırlama bağlantısı gönderildi. Lütfen e-posta kutunuzu (ve spam klasörünüzü) kontrol edin.'
      );
    } catch (err: any) {
      console.error('Password reset request error:', err);
      setError(err?.message || 'Şifre sıfırlama maili gönderilirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);

    if (!isPasswordStrong) {
      setError('Şifreniz güvenlik politikalarına uymuyor. Lütfen tüm şifre kriterlerini karşılayın.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Girdiğiniz şifreler eşleşmiyor.');
      return;
    }

    setIsLoading(true);
    try {
      await authService.updatePassword(password);
      setInfoMessage('Şifreniz başarıyla değiştirildi! Yeni şifrenizle giriş yapabilirsiniz.');
      setTimeout(() => {
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setInfoMessage('Şifreniz başarıyla güncellendi. Lütfen yeni şifrenizle giriş yapın.');
      }, 2500);
    } catch (err: any) {
      setError(err?.message || 'Şifre güncellenirken bir hata oluştu. Bağlantı süresi dolmuş veya önceden kullanılmış olabilir.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);

    if (mode === 'forgot-password') {
      return handleForgotPassword(e);
    }
    if (mode === 'reset-password') {
      return handleResetPassword(e);
    }

    // Şifre ve KVKK şartları kontrolü (Kayıt modlarında)
    if (mode !== 'login' && mode !== 'school' && mode !== 'admin') {
      if (!firstName.trim() || !lastName.trim()) {
        setError('Lütfen adınızı ve soyadınızı girin.');
        return;
      }
      if (!email.trim()) {
        setError('Lütfen e-posta adresinizi girin.');
        return;
      }
      if (!phone.trim()) {
        setError('Lütfen telefon numaranızı girin.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Şifreler eşleşmiyor.');
        return;
      }
      if (!agreeTerms) {
        setError('Lütfen kullanım şartlarını kabul edin.');
        return;
      }
    }

    setIsLoading(true);
    try {
      if (mode === 'admin') {
        if (adminEmail !== ADMIN_EMAIL || adminPassword !== ADMIN_PASSWORD) {
          setError('Admin e-posta veya şifresi hatalı.');
          setIsLoading(false);
          return;
        }
        const { user } = await authService.signInWithPassword(adminEmail, adminPassword);
        const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
        if (profile && profile.role !== 'admin') {
          await authService.signOut();
          setError('Bu sekmeden sadece Sistem Yöneticisi (Admin) hesabı ile giriş yapılabilir.');
          return;
        }
      } else if (mode === 'school') {
        const { user } = await authService.signInWithPassword(email, password);
        const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
        if (profile && profile.role !== 'school') {
          await authService.signOut();
          setError('Bu sekmeden sadece Okul Yetkilisi hesabı ile giriş yapılabilir. Lütfen ilgili sekmeden giriş yapın.');
          return;
        }
      } else if (mode === 'login') {
        const { user } = await authService.signInWithPassword(email, password);
        const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
        if (profile?.role === 'admin') {
          await authService.signOut();
          setError('Admin hesapları bu sekmeden giriş yapamaz. Lütfen "Admin Girişi" sekmesini kullanın.');
          return;
        }
        if (profile?.role === 'school') {
          await authService.signOut();
          setError('Okul hesapları bu sekmeden giriş yapamaz. Lütfen "Okul Girişi" sekmesini kullanın.');
          return;
        }
      } else {
        const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
        if (!fullName) throw new Error('Lütfen ad ve soyad alanlarını doldurun.');
        if (!phone.trim()) throw new Error('Lütfen telefon numaranızı girin.');

        if (mode === 'register-student') {
          const ageNum = Number(age);
          if (!ageNum || ageNum < 4 || ageNum > 18) throw new Error('Lütfen geçerli bir yaş girin (4-18).');
          if (!schoolName.trim()) throw new Error('Lütfen okul adınızı girin.');
          if (!city.trim()) throw new Error('Lütfen il bilginizi girin.');
          
          await authService.signUpStudent({ 
            email: email.trim(), 
            password, 
            fullName, 
            phone: phone.trim(),
            age: ageNum, 
            schoolName: schoolName.trim(), 
            city: city.trim(),
            interests: interests.trim() 
          });
          setInfoMessage('Kayıt başarılı! E-postanızı kontrol edin.');
        } else {
          await authService.signUpParent({ 
            email: email.trim(), 
            password, 
            fullName, 
            phone: phone.trim() 
          });
          setInfoMessage('Kayıt başarılı! E-postanızı kontrol edin.');
        }
      }
    } catch (err: any) {
      const msg: string = err?.message || 'İşlem gerçekleştirilemedi.';
      if (err?.isSmtpError) {
        // SMTP gecikmelerinde kullanıcıya açıklayıcı bilgi ver
        setInfoMessage(msg);
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleOAuth(provider: 'google' | 'github') {
    setError(null);
    try {
      if (provider === 'google') await authService.signInWithGoogle();
      else await authService.signInWithGitHub();
    } catch (err: any) {
      setError(err?.message || 'Sosyal hesapla giriş yapılırken bir hata oluştu.');
    }
  }

  return (
    <div className="auth-page">
      {/* Animated WebGL Mesh Drift Shader Background */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.62, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <ShaderBackground className="w-full h-full" />
      </div>
      {/* Background radial overlays */}
      <div className="auth-glow-top"></div>
      <div className="auth-glow-bottom"></div>

      <div className="auth-card">
        {/* Back Link to Landing */}
        <Link to="/" className="auth-back-link">
          <ArrowLeft size={16} /> Anasayfaya Dön
        </Link>

        {/* Logo & Header */}
        <div className="auth-header">
          <div className="auth-logo-row">
            <LogoSVG />
            <div className="auth-logo-text-col">
              <span className="auth-logo-main">Minik<span className="auth-logo-highlight">Coder</span></span>
              <span className="auth-logo-sub">AKADEMİ</span>
            </div>
          </div>

          {mode === 'admin' ? (
            <>
              <h2 className="auth-title">Sistem Yöneticisi</h2>
              <p className="auth-subtitle">Admin paneline güvenli erişim sağlayın</p>
            </>
          ) : mode === 'school' ? (
            <>
              <h2 className="auth-title">Okul Girişi</h2>
              <p className="auth-subtitle">Kurumsal okul panelinize erişim sağlayın</p>
            </>
          ) : mode === 'forgot-password' ? (
            <>
              <h2 className="auth-title">Şifremi Unuttum</h2>
              <p className="auth-subtitle">E-posta adresinizi girerek güvenli sıfırlama bağlantısı talep edin</p>
            </>
          ) : mode === 'reset-password' ? (
            <>
              <h2 className="auth-title">Yeni Şifre Belirle</h2>
              <p className="auth-subtitle">Hesabınız için güçlü ve güvenli yeni bir şifre girin</p>
            </>
          ) : mode === 'login' ? (
            <>
              <h2 className="auth-title">Tekrar Hoş Geldin!</h2>
              <p className="auth-subtitle">Hesabına giriş yaparak öğrenmeye devam et</p>
            </>
          ) : (
            <>
              <h2 className="auth-title">Yeni Hesap Oluştur</h2>
              <p className="auth-subtitle">Yapay zeka destekli eğitim yolculuğuna başla</p>
            </>
          )}
        </div>

        {/* Primary Tabs (Except Password Reset / Forgot flows) */}
        {mode !== 'forgot-password' && mode !== 'reset-password' && (
          <div className="auth-main-tabs">
            <button className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setError(null); setInfoMessage(null); }}>
              Giriş Yap
            </button>
            <button className={mode === 'register-student' || mode === 'register-parent' ? 'active' : ''} onClick={() => { setMode('register-student'); setError(null); setInfoMessage(null); }}>
              Kayıt Ol
            </button>
            <button className={mode === 'school' ? 'active' : ''} onClick={() => { setMode('school'); setError(null); setInfoMessage(null); }}>
              Okul Girişi
            </button>
            <button className={`admin-tab ${mode === 'admin' ? 'active' : ''}`} onClick={() => { setMode('admin'); setError(null); setInfoMessage(null); }}>
              Admin
            </button>
          </div>
        )}

        {/* Signup sub-tabs (Student vs Parent) */}
        {(mode === 'register-student' || mode === 'register-parent') && (
          <div className="auth-tab-segment">
            <button
              type="button"
              className={mode === 'register-student' ? 'active' : ''}
              onClick={() => setMode('register-student')}
            >
              🧒 Öğrenci Kaydı
            </button>
            <button
              type="button"
              className={mode === 'register-parent' ? 'active' : ''}
              onClick={() => setMode('register-parent')}
            >
              🛡️ Veli Kaydı
            </button>
          </div>
        )}

        {/* Form Submissions */}
        {mode === 'forgot-password' ? (
          /* STEP 1, 2 & 3: FORGOT PASSWORD FORM */
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label>Hesabınıza Bağlı E-posta Adresi *</label>
              <div className="auth-input-wrapper">
                <Mail className="auth-input-icon" />
                <input
                  type="email"
                  placeholder="ornek@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && <p className="auth-error">{error}</p>}
            {infoMessage && <p className="auth-info">{infoMessage}</p>}

            <button type="submit" className="auth-submit-btn" disabled={isLoading}>
              {isLoading ? 'Gönderiliyor...' : 'Şifre Sıfırlama Bağlantısı Gönder'}
            </button>

            <button
              type="button"
              className="auth-back-link-btn"
              onClick={() => { setMode('login'); setError(null); setInfoMessage(null); }}
              style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              ← Giriş Ekranına Dön
            </button>
          </form>
        ) : mode === 'reset-password' ? (
          /* STEP 4, 5 & 6: RESET PASSWORD FORM */
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label>Yeni Şifre *</label>
              <div className="auth-input-wrapper">
                <Lock className="auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="En az 8 karakter..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="auth-input-group">
              <label>Yeni Şifre (Tekrar) *</label>
              <div className="auth-input-wrapper">
                <Lock className="auth-input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Şifreyi tekrar girin..."
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Güvenlik Politikaları (Step 5) */}
            <div className="password-policy-box">
              <div className="password-policy-title">Güvenlik Politikası Kriterleri:</div>
              <div className="password-policy-list">
                <div className={`password-policy-item ${isMinLength ? 'valid' : ''}`}>
                  {isMinLength ? <Check size={14} /> : <X size={14} />} En az 8 karakter
                </div>
                <div className={`password-policy-item ${hasUpper ? 'valid' : ''}`}>
                  {hasUpper ? <Check size={14} /> : <X size={14} />} En az 1 büyük harf (A-Z)
                </div>
                <div className={`password-policy-item ${hasNumber ? 'valid' : ''}`}>
                  {hasNumber ? <Check size={14} /> : <X size={14} />} En az 1 rakam (0-9)
                </div>
                <div className={`password-policy-item ${hasSpecial ? 'valid' : ''}`}>
                  {hasSpecial ? <Check size={14} /> : <X size={14} />} En az 1 özel karakter (!@#$%^&*)
                </div>
              </div>
            </div>

            {error && <p className="auth-error">{error}</p>}
            {infoMessage && <p className="auth-info">{infoMessage}</p>}

            <button type="submit" className="auth-submit-btn" disabled={isLoading || !isPasswordStrong}>
              {isLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle ve Kaydet'}
            </button>
          </form>
        ) : mode === 'admin' ? (
          /* ADMIN LOGIN FORM */
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-admin-badge">
              <ShieldAlert size={16} />
              <span>Yetkisiz erişim denemeleri tespit edilir ve loglanır.</span>
            </div>
            <div className="auth-input-group">
              <label>Yönetici E-posta</label>
              <div className="auth-input-wrapper">
                <Mail className="auth-input-icon" />
                <input
                  type="email"
                  placeholder="admin@minikcoder.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="auth-input-group">
              <label>Şifre</label>
              <div className="auth-input-wrapper">
                <Lock className="auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="*********"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-submit-btn admin-submit" disabled={isLoading}>
              {isLoading ? 'Giriş yapılıyor...' : 'Yönetim Paneline Gir'}
            </button>
          </form>
        ) : mode === 'school' ? (
          /* SCHOOL LOGIN FORM */
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-school-badge">
              <ShieldAlert size={16} />
              <span>Okul kayıtları sadece sistem yöneticisi (Admin) tarafından yapılmaktadır.</span>
            </div>
            <div className="auth-input-group">
              <label>Okul Kurumsal E-posta</label>
              <div className="auth-input-wrapper">
                <Mail className="auth-input-icon" />
                <input
                  type="email"
                  placeholder="kolej@okul.k12.tr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="auth-input-group">
              <label>Şifre</label>
              <div className="auth-input-wrapper">
                <Lock className="auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="*********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-submit-btn school-submit" disabled={isLoading}>
              {isLoading ? 'Giriş yapılıyor...' : 'Okul Paneline Giriş Yap'}
            </button>
          </form>
        ) : (
          /* STUDENT & PARENT AUTH FORM */
          <form onSubmit={handleSubmit} className="auth-form">
            {/* Ad Soyad fields for registering */}
            {mode !== 'login' && (
              <div className="auth-row-split">
                <div className="auth-input-group">
                  <label>Ad *</label>
                  <div className="auth-input-wrapper">
                    <User className="auth-input-icon" />
                    <input
                      type="text"
                      placeholder="Ahmet"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="auth-input-group">
                  <label>Soyad *</label>
                  <div className="auth-input-wrapper">
                    <User className="auth-input-icon" />
                    <input
                      type="text"
                      placeholder="Yılmaz"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={mode !== 'login' && mode !== 'school' && mode !== 'admin' ? 'auth-row-split' : ''}>
              <div className="auth-input-group">
                <label>E-posta Adresi *</label>
                <div className="auth-input-wrapper">
                  <Mail className="auth-input-icon" />
                  <input
                    type="email"
                    placeholder="isim@ornek.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              {mode !== 'login' && mode !== 'school' && mode !== 'admin' && (
                <div className="auth-input-group">
                  <label>Telefon Numarası *</label>
                  <div className="auth-input-wrapper">
                    <Phone className="auth-input-icon" size={16} />
                    <input
                      type="tel"
                      placeholder="05XX XXX XX XX"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            {mode === 'login' || mode === 'school' ? (
              <div className="auth-input-group">
                <label>Şifre</label>
                <div className="auth-input-wrapper">
                  <Lock className="auth-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="*********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="auth-row-split">
                <div className="auth-input-group">
                  <label>Şifre *</label>
                  <div className="auth-input-wrapper">
                    <Lock className="auth-input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="*********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="auth-input-group">
                  <label>Şifre Tekrar *</label>
                  <div className="auth-input-wrapper">
                    <Lock className="auth-input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="*********"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Extra student fields */}
            {mode === 'register-student' && (
              <>
                <div className="auth-row-split">
                  <div className="auth-input-group">
                    <label>Yaş *</label>
                    <div className="auth-input-wrapper">
                      <Smile className="auth-input-icon" size={16} />
                      <input
                        type="number"
                        placeholder="10"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        min={4}
                        max={18}
                        required
                      />
                    </div>
                  </div>
                  <div className="auth-input-group">
                    <label>İl *</label>
                    <div className="auth-input-wrapper">
                      <MapPin className="auth-input-icon" size={16} />
                      <input
                        type="text"
                        placeholder="İstanbul"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="auth-input-group">
                  <label>Okul Adı *</label>
                  <div className="auth-input-wrapper">
                    <Building2 className="auth-input-icon" size={16} />
                    <input
                      type="text"
                      placeholder="Örn: Atatürk İlkokulu"
                      value={schoolName}
                      onChange={(e) => setSchoolName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="auth-input-group">
                  <label>İlgi Alanları (virgülle ayırın)</label>
                  <div className="auth-input-wrapper">
                    <Compass className="auth-input-icon" size={16} />
                    <input
                      type="text"
                      placeholder="Örn: Kodlama, Matematik, Uzay"
                      value={interests}
                      onChange={(e) => setInterests(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Remember me & terms */}
            {mode === 'login' || mode === 'school' ? (
              <div className="auth-checkbox-split">
                <label className="auth-checkbox-row">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Beni Hatırla</span>
                </label>
                <button
                  type="button"
                  className="auth-forgot-link-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    setMode('forgot-password');
                    setError(null);
                    setInfoMessage(null);
                  }}
                >
                  Şifremi Unuttum?
                </button>
              </div>
            ) : (
              <label className="auth-checkbox-row" style={{ marginTop: 4 }}>
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  required
                />
                <span>Kullanım Şartları ve KVKK Sözleşmesini kabul ediyorum.</span>
              </label>
            )}

            {error && <p className="auth-error">{error}</p>}
            {infoMessage && <p className="auth-info">{infoMessage}</p>}

            <button type="submit" className="auth-submit-btn" disabled={isLoading}>
              {isLoading ? 'Bekleyin...' : (mode === 'login' || mode === 'school') ? 'Giriş Yap' : 'Hesap Oluştur'}
            </button>
          </form>
        )}

        {/* OAuth Integration (Except Admin, School, Forgot Password, Reset Password) */}
        {mode !== 'admin' && mode !== 'school' && mode !== 'forgot-password' && mode !== 'reset-password' && (
          <>
            <div className="auth-divider-line">VEYA</div>
            <div className="auth-oauth-split">
              <button type="button" className="auth-oauth-btn" onClick={() => handleOAuth('google')}>
                <span className="oauth-g-icon">G</span> Google ile giriş yap
              </button>
            </div>
            <div className="auth-footer">
              {mode === 'login' ? (
                <>
                  Hesabın yok mu?{' '}
                  <a
                    href="#register"
                    className="auth-footer-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setMode('register-student');
                      setError(null);
                      setInfoMessage(null);
                    }}
                  >
                    Kayıt ol
                  </a>
                </>
              ) : (
                <>
                  Zaten hesabın var mı?{' '}
                  <a
                    href="#login"
                    className="auth-footer-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setMode('login');
                      setError(null);
                      setInfoMessage(null);
                    }}
                  >
                    Giriş yap
                  </a>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
